// react 19 — use() reads context
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { markPaymentFailureAutoReturn } from '|/app/purchase/checkout/payment-failure-auto-return';
import { FormStateContext, UserInitializationContext } from '|/context';
import { currency } from '|/helpers';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { useRefreshDashboardSummary } from '|/hooks/use-dashboard-summary-refresh';
import useEventHelper from '|/hooks/use-event-helper';
import { clearScopedSlotPreference } from '|/hooks/use-slot-preference';
import type { PaymentOnPurchase } from '|/services/purchase-payments';
import { useNativePlaid } from './plaid-modal';
import { isDigitalWalletSelection, isPayLaterSelection } from './utils/digital-wallet';
import {
  getCardPaymentIds,
  getNewFailedPayment,
  waitForCardPaymentResolution,
  type CardPaymentResolution
} from './utils/wait-for-card-payment-resolution';
import { usePaymentContext } from '../../contexts/payment';
import { collectPayment, fetchPaymentSummary, submitConsents } from '../../services';
import type { CompletePurchaseSubmitResult } from '../../use-complete-purchase-vm';

export const getPayAtOrderButtonText = (amountDue: number, isPayLater: boolean) =>
  amountDue > 0 && !isPayLater
    ? `Pay ${currency(amountDue).replace('.00', '')} now`
    : 'Place order';

const minimumDigitalWalletAmount = 1;

interface UsePayAtOrderOptions {
  onSubmit: () => Promise<CompletePurchaseSubmitResult>;
  editPaymentMethod: () => void;
  buttonText?: string;
}

export const usePayAtOrder = ({
  onSubmit,
  editPaymentMethod,
  buttonText: buttonTextOverride
}: UsePayAtOrderOptions) => {
  const {
    state: { paymentSummary },
    dispatch: paymentDispatch
  } = usePaymentContext();
  const {
    shouldDisableWhileImpersonating,
    shouldInitiateRequestForPayment,
    shouldCollectPayment,
    requestForPaymentAmount,
    isCardEligible,
    purchasePaymentBreakdown,
    paymentSelection,
    consents
  } = paymentSummary;

  const { isImpersonating: isUserBeingImpersonated } = use(UserInitializationContext);
  const { trackEvent } = useAnalyticsContext();
  const { sendEvent } = useEventHelper();
  const { setSubmitError } = use(FormStateContext);
  const refreshDashboardSummary = useRefreshDashboardSummary();
  const submitAbortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      submitAbortRef.current?.abort();
    },
    []
  );

  const [hasCustomerConfirmedSufficientFunds, setHasCustomerConfirmedSufficientFunds] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDigitalWalletSupported, setIsDigitalWalletSupported] = useState(false);
  const [isPlaidModalOpen, setIsPlaidModalOpen] = useState(false);
  const [hasPurchaseRolledBack, setHasPurchaseRolledBack] = useState(false);

  const hasDigitalWalletSelection = isDigitalWalletSelection(paymentSelection);
  const shouldUseDigitalWallet =
    hasDigitalWalletSelection && purchasePaymentBreakdown.amountDue >= minimumDigitalWalletAmount;
  const isPayLater = isPayLaterSelection(paymentSelection);
  const isDigitalWalletAvailable = isDigitalWalletSupported && isCardEligible;
  const shouldBlockImpersonatedSubmit = shouldDisableWhileImpersonating && !isPayLater;
  const shouldShowConsents = !shouldUseDigitalWallet || isDigitalWalletAvailable;
  const shouldShowImpersonationText =
    isUserBeingImpersonated && (shouldBlockImpersonatedSubmit || shouldUseDigitalWallet);
  const shouldRenderDigitalWallets =
    shouldUseDigitalWallet && !isUserBeingImpersonated && !hasPurchaseRolledBack;
  const shouldRequireSufficientFunds =
    purchasePaymentBreakdown.amountDue > 0 &&
    paymentSelection?.selectionType === 'BankAccount' &&
    !!paymentSelection.bankAccount?.accountMask;

  useEffect(() => {
    setHasCustomerConfirmedSufficientFunds(!shouldRequireSufficientFunds);
  }, [shouldRequireSufficientFunds]);

  const isButtonDisabled =
    isSubmitting ||
    hasPurchaseRolledBack ||
    !hasCustomerConfirmedSufficientFunds ||
    shouldShowImpersonationText;
  const buttonText =
    buttonTextOverride ?? getPayAtOrderButtonText(purchasePaymentBreakdown.amountDue, isPayLater);
  const consentTypes = useMemo(
    () => consents.map(({ consentType, consentSubType }) => ({ consentType, consentSubType })),
    [consents]
  );

  const handleSubmit = useCallback(async () => {
    await onSubmit();
  }, [onSubmit]);

  const handleError = useCallback(() => {
    setSubmitError(new Error('Failed to collect payment'));
  }, [setSubmitError]);

  const onPlaidExit = useCallback(() => {
    /* re-show PAO inline — no-op since it's already visible */
  }, []);

  const { isNativeApp, openNativePlaid } = useNativePlaid({
    onSuccess: handleSubmit,
    onExit: onPlaidExit,
    onError: handleError
  });

  const openPlaidModal = isNativeApp
    ? openNativePlaid
    : () => {
        setIsPlaidModalOpen(true);
      };

  const onCardAdded = useCallback(async () => {
    const summary = await fetchPaymentSummary();
    paymentDispatch({
      type: 'SET_IS_CARD_ELIGIBLE',
      payload: summary.isCardEligible
    });
    return summary;
  }, [paymentDispatch]);

  const onDigitalWalletPaymentCollected = useCallback(async () => {
    trackEvent('pp_complete_purchase_pao_submit', {
      isUserBeingImpersonated,
      consentTypes,
      shouldInitiateRequestForPayment,
      shouldCollectPayment,
      requestForPaymentAmount,
      purchasePaymentBreakdown,
      paymentSelection,
      buttonText
    });

    const summary = await fetchPaymentSummary();
    paymentDispatch({ type: 'SET_PAYMENT_SUMMARY', payload: summary });

    return onSubmit();
  }, [
    buttonText,
    consentTypes,
    isUserBeingImpersonated,
    onSubmit,
    paymentDispatch,
    paymentSelection,
    purchasePaymentBreakdown,
    requestForPaymentAmount,
    shouldCollectPayment,
    shouldInitiateRequestForPayment,
    trackEvent
  ]);

  const trackCardPaymentResolution = (resolution: CardPaymentResolution) => {
    const payment = resolution.status === 'timed_out' ? undefined : resolution.payment;

    sendEvent('pp_info', {
      analyticsId: 'pp_pao_payment_status_resolved',
      status: resolution.status,
      failureCode: payment?.failureCode,
      paymentId: payment?.paymentId,
      secondsToResolve: resolution.secondsToResolve,
      pollAttempts: resolution.pollAttempts
    });
  };

  const returnToPaymentStep = (
    failedPayment: PaymentOnPurchase | undefined,
    failedAt: 'collect_payment' | 'charge_poll' | 'digital_wallet'
  ) => {
    sendEvent('pp_info', {
      analyticsId: 'pp_payment_failed_auto_return',
      failureCode: failedPayment?.failureCode,
      paymentId: failedPayment?.paymentId,
      fromStep: 'complete-purchase',
      failedAt
    });
    if (failedPayment) markPaymentFailureAutoReturn(failedPayment.paymentId);
    refreshDashboardSummary();
    setIsSubmitting(false);
    editPaymentMethod();
  };

  const onPurchaseRolledBack = () => {
    setHasPurchaseRolledBack(true);
    clearScopedSlotPreference();

    submitAbortRef.current?.abort();
    const controller = new AbortController();
    submitAbortRef.current = controller;

    void getNewFailedPayment(null, controller.signal)
      .catch(() => undefined)
      .then(failedPayment => {
        returnToPaymentStep(failedPayment, 'digital_wallet');
      });
  };

  const submitPayAtOrder = async (): Promise<CompletePurchaseSubmitResult> => {
    setIsSubmitting(true);

    submitAbortRef.current?.abort();
    const controller = new AbortController();
    submitAbortRef.current = controller;

    trackEvent('pp_complete_purchase_pao_submit', {
      isUserBeingImpersonated,
      consentTypes,
      shouldInitiateRequestForPayment,
      shouldCollectPayment,
      requestForPaymentAmount,
      purchasePaymentBreakdown,
      paymentSelection,
      buttonText
    });

    try {
      if (isUserBeingImpersonated) {
        if (shouldBlockImpersonatedSubmit) {
          throw new Error('Disabled while impersonating');
        }

        const { didNavigate } = await onSubmit();
        setIsSubmitting(didNavigate);
        return { didNavigate };
      }

      if (shouldCollectPayment) {
        const previousCardPaymentIds =
          paymentSelection?.selectionType === 'Card'
            ? await getCardPaymentIds(controller.signal).catch(() => null)
            : null;
        if (controller.signal.aborted) return { didNavigate: false };

        const { paymentCollectionSucceeded } = await collectPayment(consentTypes);

        if (!paymentCollectionSucceeded) {
          setHasPurchaseRolledBack(true);
          clearScopedSlotPreference();
          const failedPayment = await getNewFailedPayment(
            previousCardPaymentIds,
            controller.signal
          ).catch(() => undefined);
          returnToPaymentStep(failedPayment, 'collect_payment');
          return { didNavigate: true };
        }

        // The card is charged by now, so an unmount that aborts the poll must still
        // place the order, as it would on a timeout.
        const resolution = previousCardPaymentIds
          ? await waitForCardPaymentResolution(previousCardPaymentIds, controller.signal).catch(
              () => null
            )
          : null;

        if (resolution) {
          trackCardPaymentResolution(resolution);

          if (resolution.status === 'Failed') {
            returnToPaymentStep(resolution.payment, 'charge_poll');
            return { didNavigate: true };
          }
        }

        const { didNavigate } = await onSubmit();
        setIsSubmitting(didNavigate);
        return { didNavigate };
      }

      if (consentTypes.length > 0) {
        await submitConsents(consentTypes);
      }

      if (shouldInitiateRequestForPayment) {
        openPlaidModal();
        setIsSubmitting(false);
        return { didNavigate: true };
      }

      const { didNavigate } = await onSubmit();
      setIsSubmitting(didNavigate);
      return { didNavigate };
    } catch {
      if (controller.signal.aborted) return { didNavigate: false };
      setIsSubmitting(false);
      handleError();
      return { didNavigate: false };
    }
  };

  return {
    paymentSummary,
    purchasePaymentBreakdown,
    paymentSelection,
    requestForPaymentAmount,
    isCardEligible,
    isSubmitting,
    isButtonDisabled,
    hasPurchaseRolledBack,
    isDigitalWalletAvailable,
    isDigitalWalletSupported,
    isPlaidModalOpen,
    isUserBeingImpersonated,
    shouldUseDigitalWallet,
    shouldRenderDigitalWallets,
    hasDigitalWalletSelection,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment,
    hasCustomerConfirmedSufficientFunds,
    buttonText,
    consentTypes,
    setHasCustomerConfirmedSufficientFunds,
    setIsSubmitting,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onCardAdded,
    onDigitalWalletPaymentCollected,
    onPurchaseRolledBack,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  };
};

export type PayAtOrderController = ReturnType<typeof usePayAtOrder>;
